/**
 * Ask AI on a Study Anything book: the book tutor (../Books/BookTutor) rewired
 * to one of the user's own uploaded books.
 *
 * Nothing about the chat changes — one conversation per book, questions as
 * polled jobs, a selected passage sent with the question, tabs branched from
 * passages. What changes is underneath: the tutor searches the book with grep
 * for every question instead of an index, so an answer's sources are the lines
 * it read (`{kind: "lines", start, end, title}`), labelled by the heading they
 * fall under; a click scrolls the book to that heading. A book is private:
 * there is no mentor review tab.
 */

import React, { useMemo } from "react";

import TutorTabs from "../Tutor/TutorTabs";
import {
  askStudy, askStudyThread, clearStudy, createStudyThread, getStudyHistory, getStudyJob, getStudyStatus,
  getStudyThreadHistory, getStudyThreads, updateStudyThread,
} from "./studyApi";

/** A grep-agent source: the lines of the book the tutor read. */
export const isLinesSource = (s) => s.kind === "lines";

export default function StudyTutor({ bookId, place, quote, onClearQuote, onSource }) {
  const api = useMemo(() => ({
    ask: (body) => askStudy(bookId, body),
    job: (token) => getStudyJob(bookId, token),
    history: (before) => getStudyHistory(bookId, before),
    clear: () => clearStudy(bookId),
    status: () => getStudyStatus(bookId),
    // Side conversations: tabs branched from a passage of an answer or of the book.
    threads: () => getStudyThreads(bookId),
    createThread: (body) => createStudyThread(bookId, body),
    updateThread: (id, patch) => updateStudyThread(bookId, id, patch),
    askThread: (id, body) => askStudyThread(bookId, id, body),
    threadHistory: (id, before) => getStudyThreadHistory(bookId, id, before),
  }), [bookId]);

  /** Where they are: the heading of the selected passage if any, else the heading being read. */
  const context = () => {
    const here = place ? place() : null;
    const section = quote?.section || here?.section || "";
    return { payload: section ? { section } : {}, bubble: {} };
  };

  return (
    <TutorTabs api={api} pendingId={`study-${bookId}`} context={context} onSource={onSource}
      isClickable={isLinesSource} quote={quote} height="100%" onClearQuote={onClearQuote}
      labels={{
        online: "Ask about this book",
        offline: "The tutor is offline right now",
        notReady: "The tutor hasn't opened this book yet — your first question fetches it",
        empty: "Ask anything about this book — or select a passage to ask about it. The tutor searches the book for every question, the way grep would.",
        footer: "Select text in the book or in an answer to ask about it. Only you can see this conversation.",
        clearBody: "The tutor starts fresh for this book, and its tabs close with it.",
      }} />
  );
}
